import React, { useEffect, useRef, useState } from 'react';
import type { ConversationMetadata } from '../types';
import { COLOR, FONT } from '../styles/tokens';

export function ConversationControls({
  metadata,
  disabled,
  onSettings,
  onShare,
  onExport,
  onDelete,
}: {
  metadata: ConversationMetadata | null;
  disabled: boolean;
  onSettings: (settings: {
    visibility: 'private' | 'shared';
    temporary: boolean;
    retentionDays: number | null;
  }) => Promise<void>;
  onShare: () => Promise<void>;
  onExport: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    try {
      await operation();
    } finally {
      setBusy(false);
    }
  };

  const label = metadata?.temporary
    ? `Temporary · ${metadata.retentionDays ?? 1}d`
    : metadata?.visibility === 'shared'
    ? 'Shared'
    : 'Private · durable';

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(value => !value)}
        style={button}
        title="Conversation privacy and retention"
      >
        {metadata?.visibility === 'shared' ? '◉' : '🔒'} {label} ▾
      </button>
      {open && (
        <div style={menu}>
          <strong>Conversation</strong>
          <div style={{ color: COLOR.textMuted, fontSize: FONT.size.xs }}>
            {metadata?.temporary
              ? `Retained for ${metadata.retentionDays ?? 1} day(s)`
              : 'Retained until you delete it'}
          </div>
          <button
            type="button"
            disabled={busy}
            style={menuButton}
            onClick={() =>
              run(() =>
                onSettings({
                  visibility: 'private',
                  temporary: false,
                  retentionDays: null,
                }),
              )
            }
          >
            🔒 Private and durable
          </button>
          <button
            type="button"
            disabled={busy}
            style={menuButton}
            onClick={() =>
              run(() =>
                onSettings({
                  visibility: 'private',
                  temporary: true,
                  retentionDays: 1,
                }),
              )
            }
          >
            ◷ Temporary · 1 day
          </button>
          <button
            type="button"
            disabled={busy}
            style={menuButton}
            onClick={() => {
              const prompt =
                metadata?.visibility === 'shared'
                  ? 'This conversation was already shared. Copying a new link will stop the previous link from working. Continue?'
                  : 'Create a share link for this conversation?';
              if (!window.confirm(prompt)) return;
              void run(onShare);
            }}
          >
            ↗ Share…
          </button>
          <button
            type="button"
            disabled={busy}
            style={menuButton}
            onClick={() => run(onExport)}
          >
            ↓ Export
          </button>
          <button
            type="button"
            disabled={busy}
            style={{ ...menuButton, color: COLOR.red }}
            onClick={() => {
              if (
                !window.confirm(
                  'Permanently delete this conversation? This cannot be undone.',
                )
              )
                return;
              void run(onDelete);
            }}
          >
            Delete conversation
          </button>
        </div>
      )}
    </div>
  );
}

const button: React.CSSProperties = {
  height: 30,
  padding: '0 8px',
  border: `1px solid ${COLOR.borderSubtle}`,
  borderRadius: 8,
  background: 'transparent',
  color: COLOR.textSecondary,
  cursor: 'pointer',
  fontFamily: FONT.sans,
  fontSize: FONT.size.xs,
};

const menu: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 7px)',
  right: 0,
  zIndex: 20,
  width: 230,
  display: 'grid',
  gap: 5,
  padding: 10,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 12,
  background: COLOR.bgRaised,
  boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
};

const menuButton: React.CSSProperties = {
  width: '100%',
  padding: '7px 5px',
  border: 0,
  borderRadius: 7,
  background: 'transparent',
  color: COLOR.textPrimary,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: FONT.sans,
  fontSize: FONT.size.sm,
};
